import React, { useEffect, useRef, useState } from 'react';
import { Previewer } from 'pagedjs';

const pagedCssUrl = '/print/pagedPrint.css';

/**
 * Renders `children` through PagedJS so long content (Terms & Conditions, itineraries)
 * paginates correctly with a properly repeating footer — instead of relying on the
 * browser's native print engine, which handles `position: fixed` footers + long flowing
 * text inconsistently (content can render behind the footer instead of moving to the
 * next page).
 *
 * Usage: put the real printable markup inside <PagedPrintView>, including one element
 * with className="paged-footer" as the FIRST child (PagedJS only starts repeating a
 * "running" element on pages after it first encounters it in the source).
 */
export default function PagedPrintView({ children, docKey }) {
  const sourceRef = useRef();
  const targetRef = useRef();
  const [rendering, setRendering] = useState(true);
  // Running two concurrent PagedJS Previewer instances (which happens if we naively
  // start a fresh render on every effect invocation) causes it to hang indefinitely —
  // React 18 StrictMode double-invokes effects in dev, so we guard against ever
  // starting a second real render for the same docKey.
  const startedForKey = useRef(null);

  useEffect(() => {
    if (!sourceRef.current || !targetRef.current) return;
    if (startedForKey.current === docKey) return;
    startedForKey.current = docKey;
    setRendering(true);

    // Render into an isolated, off-screen container (not the shared targetRef) so the
    // final swap into visible DOM is atomic. Still has to be attached to the document
    // (not detached) for PagedJS's internal getBoundingClientRect() calls to work.
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.visibility = 'hidden';
    tempContainer.style.top = '0';
    tempContainer.style.left = '0';
    document.body.appendChild(tempContainer);

    const paged = new Previewer();
    paged.preview(sourceRef.current.innerHTML, [pagedCssUrl], tempContainer).then(() => {
      if (startedForKey.current !== docKey || !targetRef.current) {
        tempContainer.remove();
        return;
      }
      targetRef.current.innerHTML = '';
      while (tempContainer.firstChild) {
        targetRef.current.appendChild(tempContainer.firstChild);
      }
      tempContainer.remove();
      setRendering(false);
    });
  }, [docKey]);

  return (
    <div>
      {/* Hidden source — PagedJS reads its HTML, then we render the real paginated
          output separately. Never shown or printed directly. */}
      <div ref={sourceRef} style={{ display: 'none' }}>
        <div className="paged-content">{children}</div>
      </div>

      {rendering && (
        <div className="no-print" style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
          Preparing print layout…
        </div>
      )}
      {/* Must stay in normal layout flow (never display:none) while PagedJS renders
          into it — it calls getBoundingClientRect() on this element during layout,
          which returns null/breaks if the element isn't actually laid out in the DOM.
          Use visibility (keeps layout box) instead of display to hide it meanwhile. */}
      <div ref={targetRef} style={{ visibility: rendering ? 'hidden' : 'visible', height: rendering ? 0 : 'auto', overflow: rendering ? 'hidden' : 'visible' }} />
    </div>
  );
}
